import { getModule } from "./catalog";

const R = 6378137;
const D = Math.PI / 180;

export function makeFrame(polygon, azimuth = 0) {
  const n = polygon.length || 1;
  const lat0 = polygon.reduce((s, p) => s + p[0], 0) / n;
  const lng0 = polygon.reduce((s, p) => s + p[1], 0) / n;
  const a = azimuth * D, c = Math.cos(a), s = Math.sin(a);
  const kx = R * Math.cos(lat0 * D) * D, ky = R * D;
  return {
    toUV: ([lat, lng]) => {
      const x = (lng - lng0) * kx, y = (lat - lat0) * ky;
      return [c * x - s * y, s * x + c * y];
    },
    toLL: ([u, v]) => {
      const x = c * u + s * v, y = -s * u + c * v;
      return [lat0 + y / ky, lng0 + x / kx];
    },
  };
}

export const polyArea = (pts) =>
  Math.abs(pts.reduce((s, p, i) => { const q = pts[(i + 1) % pts.length]; return s + p[0] * q[1] - q[0] * p[1]; }, 0)) / 2;

export const polyPerimeter = (pts) =>
  pts.reduce((s, p, i) => { const q = pts[(i + 1) % pts.length]; return s + Math.hypot(q[0] - p[0], q[1] - p[1]); }, 0);

function inside([x, y], pts) {
  let r = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) r = !r;
  }
  return r;
}

function segDist([px, py], [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay, l = dx * dx + dy * dy;
  let t = l ? ((px - ax) * dx + (py - ay) * dy) / l : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
}

const corners = ([u0, v0, u1, v1]) => [[u0, v0], [u1, v0], [u1, v1], [u0, v1]];
const edgesOf = (poly) => poly.map((a, i) => [a, poly[(i + 1) % poly.length]]);

function fits(rect, poly, edge) {
  const cs = corners(rect);
  if (!cs.every((p) => inside(p, poly))) return false;
  for (const [a, b] of edgesOf(poly)) {
    if (a[0] > rect[0] && a[0] < rect[2] && a[1] > rect[1] && a[1] < rect[3]) return false;
    if (edge > 0) {
      if (cs.some((p) => segDist(p, a, b) < edge - 1e-6)) return false;
      for (let k = 0; k < 4; k++) if (segDist(a, cs[k], cs[(k + 1) % 4]) < edge - 1e-6) return false;
    }
  }
  return true;
}


export function obstacleRect(o, frame, pad = 0) {
  const [u, v] = frame.toUV([o.lat, o.lng]);
  const w = Number(o.w) || 0, h = Number(o.h) || 0;
  return [u - w / 2 - pad, v - h / 2 - pad, u + w / 2 + pad, v + h / 2 + pad];
}

export const rectToLL = (rect, frame) => corners(rect).map(frame.toLL);

function segSeg(a, b, c, d) {
  const o = (p, q, r) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  const o1 = o(a, b, c), o2 = o(a, b, d), o3 = o(c, d, a), o4 = o(c, d, b);
  if ((o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0)) return 0;
  return Math.min(segDist(c, a, b), segDist(d, a, b), segDist(a, c, d), segDist(b, c, d));
}

// ostacolo → { pts UV, closed }; tipi: rect (centro + w×h), poly, line
export function obstacleShape(o, frame) {
  if (o.type === "poly" || o.type === "line") return { pts: (o.points || []).map(frame.toUV), closed: o.type === "poly" };
  const r = obstacleRect(o, frame);
  return { pts: corners(r), closed: true };
}

function hitsObstacle(rect, { pts, closed }, clr) {
  if (pts.length < 2) return false;
  const cs = corners(rect);
  if (closed && pts.length >= 3 && cs.some((c) => inside(c, pts))) return true;
  if (pts.some((p) => p[0] > rect[0] && p[0] < rect[2] && p[1] > rect[1] && p[1] < rect[3])) return true;
  const segs = closed ? pts.map((a, i) => [a, pts[(i + 1) % pts.length]]) : pts.slice(1).map((b, i) => [pts[i], b]);
  return segs.some(([a, b]) => cs.some((c, k) => segSeg(a, b, c, cs[(k + 1) % 4]) < clr + 1e-9));
}

// Ombra degli ostacoli col sole del 21 dicembre alle 12:00 (caso peggiore), proiettata sul piano della falda
export function sunNoonWinter(lat) {
  return Math.max(1, 90 - Math.abs(Number(lat) || 46) - 23.44);
}

export function shadowPolys(roof, frame, lat, pitchDeg) {
  const el = sunNoonWinter(lat) * D, a = (Number(roof.azimuth) || 0) * D;
  const su = Math.sin(a) * Math.cos(el), sv = -Math.cos(a) * Math.cos(el);
  const den = Math.sin(el) - Math.tan(pitchDeg * D) * sv;
  const out = [];
  for (const o of roof.obstacles || []) {
    const h = Number(o.height) || 0;
    if (h <= 0) continue;
    const t = den > 0.01 ? Math.min(h / den, 30 / Math.cos(el)) : 30 / Math.cos(el);
    const du = -t * su, dv = -t * sv;
    const { pts, closed } = obstacleShape(o, frame);
    const segs = closed ? pts.map((p, i) => [p, pts[(i + 1) % pts.length]]) : pts.slice(1).map((q, i) => [pts[i], q]);
    for (const [p, q] of segs) out.push({ oid: o.id, pts: [p, q, [q[0] + du, q[1] + dv], [p[0] + du, p[1] + dv]] });
  }
  return out;
}

// Zone vento EN 1991-1-4: F angoli, G bordi, H interno (tetti piani: H fascia intermedia, I interno)
function windZone(rect, poly, e, flat) {
  const cs = corners(rect);
  const dEdge = Math.min(...edgesOf(poly).map(([a, b]) => Math.min(...cs.map((p) => segDist(p, a, b)))));
  const dVert = Math.min(...poly.map((v) => Math.min(...cs.map((p) => Math.hypot(p[0] - v[0], p[1] - v[1])))));
  if (dEdge < e / 10 && dVert < e / 4) return "F";
  if (dEdge < e / 10) return "G";
  if (flat) return dEdge < e / 2 ? "H" : "I";
  return "H";
}

export function moduleDims(project) {
  const m = getModule(project.modules);
  const portrait = project.modules.orientation !== "landscape";
  const L = m.length / 1000, W = m.width / 1000;
  return { m, du: portrait ? W : L, dsl: portrait ? L : W };
}

function fieldLayout(project, roof, dims) {
  const flat = project.roof_type === "piano";
  const P = project.modules;
  const frame = makeFrame(roof.polygon, Number(roof.azimuth) || 0);
  const polyUV = roof.polygon.map(frame.toUV);
  const pitchDeg = flat ? 0 : Number(roof.pitch) || 0;
  const pitch = pitchDeg * D;
  const ang = flat ? (Number(P.tilt) || 0) * D : pitch;
  const { du } = dims;
  const dv = dims.dsl * Math.cos(ang);
  const gu = Number(P.gap) || 0;
  const gv = flat ? Number(P.row_gap) || 0 : gu * Math.cos(pitch);
  const edge = Number(P.edge) || 0;
  const us = polyUV.map((p) => p[0]), vs = polyUV.map((p) => p[1]);
  const umin = Math.min(...us), umax = Math.max(...us), vmin = Math.min(...vs), vmax = Math.max(...vs);
  const e = Math.min(Math.max(umax - umin, vmax - vmin), 2 * (Number(project.building_height) || 8));
  const base = { roof, frame, polyUV, pitch: pitchDeg, e, cells: [], active: [], shadows: [], roofArea: polyArea(polyUV) / Math.cos(pitch) };
  if (du <= 0 || dv <= 0) return base;
  const obs = (roof.obstacles || []).map((o) => ({ sh: obstacleShape(o, frame), clr: o.clearance === undefined || o.clearance === null || o.clearance === "" ? Number(P.obstacle_clearance) || 0 : Number(o.clearance) }));
  const areas = moduleFieldsOf(roof).map((a) => a.map(frame.toUV));
  if (roof.fields_mode && !areas.length) return base;
  const removed = new Set(roof.removed || []);
  const shadows = shadowPolys(roof, frame, project.lat, flat ? 0 : pitchDeg);
  const cells = [];
  const su = du + gu, sv = dv + gv;
  const ou = (((Number(roof.off_u) || 0) % su) + su) % su, ov = (((Number(roof.off_v) || 0) % sv) + sv) % sv;
  const overlaps = (a, b) => a[0] < b[2] - 1e-6 && b[0] < a[2] - 1e-6 && a[1] < b[3] - 1e-6 && b[1] < a[3] - 1e-6;
  // con "Campo moduli" ogni campo ha la sua griglia, che parte dal riquadro del campo; chiave "r-c" (1° campo) o "a{i}:r-c"
  for (const [ai, areaUV] of (areas.length ? areas : [null]).entries()) {
    const bx = areaUV ? [Math.min(...areaUV.map((q) => q[0])), Math.max(...areaUV.map((q) => q[0])), Math.min(...areaUV.map((q) => q[1])), Math.max(...areaUV.map((q) => q[1]))] : null;
    const e0 = bx ? 0 : edge, [gU0, gU1, gV0, gV1] = bx || [umin, umax, vmin, vmax];
    const u0 = bx ? Math.max(gU0, umin + edge) : gU0 + e0 + (ou ? ou - su : 0), v0 = bx ? Math.max(gV0, vmin + edge) : gV0 + e0 + (ov ? ov - sv : 0);
    for (let r = 0, v = v0; v + dv <= gV1 - e0 + 1e-9 && r < 400; r++, v += sv) {
      for (let c = 0, u = u0; u + du <= gU1 - e0 + 1e-9 && c < 400; c++, u += su) {
        const rect = [u, v, u + du, v + dv];
        if (!fits(rect, polyUV, edge) || (areaUV && !fits([rect[0] + 1e-6, rect[1] + 1e-6, rect[2] - 1e-6, rect[3] - 1e-6], areaUV, 0)) || obs.some((o) => hitsObstacle(rect, o.sh, o.clr))) continue;
        if (ai > 0 && cells.some((x) => overlaps(x.rect, rect))) continue;
        const key = ai ? `a${ai}:${r}-${c}` : `${r}-${c}`;
        const shaded = shadows.some((sh) => sh.pts.length >= 3 && hitsObstacle(rect, { pts: sh.pts, closed: true }, 0));
        cells.push({ key, fid: roof.id, a: ai, r, c, rect, zone: windZone(rect, polyUV, e, flat), active: !removed.has(key), shaded });
      }
    }
  }
  return { ...base, cells, active: cells.filter((x) => x.active), shadows, gu, su, sv, du, dv, edge };
}

// campi moduli della falda (poligoni lat/lng): module_fields, oppure il vecchio module_area singolo
export function moduleFieldsOf(roof) {
  const f = (roof.module_fields || []).filter((a) => (a || []).length >= 3);
  return f.length ? f : (roof.module_area || []).length >= 3 ? [roof.module_area] : [];
}

// piano della falda in un riferimento comune (m, Est/Nord): z = k · n·(p − a), a = inizio gronda, n = normale interna
function planeOf(f, G) {
  const pts = f.roof.polygon.map(G.toUV), g = (Number(f.roof.gutter_edge) || 0) % pts.length;
  const a = pts[g], b = pts[(g + 1) % pts.length], L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  let n = [-(b[1] - a[1]) / L, (b[0] - a[0]) / L];
  const c = [pts.reduce((t, q) => t + q[0], 0) / pts.length, pts.reduce((t, q) => t + q[1], 0) / pts.length];
  if ((c[0] - a[0]) * n[0] + (c[1] - a[1]) * n[1] < 0) n = [-n[0], -n[1]];
  const k = Math.tan((Number(f.pitch) || 0) * D);
  return { pts, z: (q) => k * ((q[0] - a[0]) * n[0] + (q[1] - a[1]) * n[1]), k, n, a };
}

function raySegHit(q, d, a, b) {
  const ex = b[0] - a[0], ey = b[1] - a[1], den = d[0] * ey - d[1] * ex;
  if (Math.abs(den) < 1e-12) return null;
  const wx = a[0] - q[0], wy = a[1] - q[1];
  const s = (wx * ey - wy * ex) / den, t = (wx * d[1] - wy * d[0]) / den;
  return s > 1e-6 && t >= 0 && t <= 1 ? s : null;
}

// Ombre che superano colmo/displuvio: raggio dal punto della falda verso il sole, bloccato dal prisma dell'ostacolo (stesso edificio)
function crossShadows(project, fields) {
  const el = sunNoonWinter(project.lat) * D, dir = [0, -1], tanE = Math.tan(el);
  for (const N of fields) {
    if (!N.roof.group) continue;
    const srcs = fields.filter((F) => F !== N && F.roof.group === N.roof.group && (F.roof.obstacles || []).some((o) => Number(o.height) > 0));
    if (!srcs.length) continue;
    const G = makeFrame(N.roof.polygon, 0), PN = planeOf(N, G);
    const prisms = srcs.flatMap((F) => {
      const PF = planeOf(F, G);
      return (F.roof.obstacles || []).filter((o) => Number(o.height) > 0).map((o) => {
        const sh = obstacleShape(o, F.frame), pts = sh.pts.map((q) => G.toUV(F.frame.toLL(q)));
        const c = [pts.reduce((t, q) => t + q[0], 0) / pts.length, pts.reduce((t, q) => t + q[1], 0) / pts.length];
        const top = PF.z(c) + Number(o.height);
        const segs = sh.closed ? pts.map((p, i) => [p, pts[(i + 1) % pts.length]]) : pts.slice(1).map((q, i) => [pts[i], q]);
        return { oid: o.id, pts, segs, top, PF };
      });
    });
    const blocked = (q) => {
      const z0 = PN.z(q);
      return prisms.some((P) => P.segs.some(([a, b]) => { const s = raySegHit(q, dir, a, b); return s !== null && s < 60 && z0 + s * tanE <= P.top + 1e-9; }));
    };
    for (const c of N.cells) {
      const [u0, v0, u1, v1] = c.rect, um = (u0 + u1) / 2, vm = (v0 + v1) / 2;
      const samples = [[u0, v0], [u1, v0], [u1, v1], [u0, v1], [um, vm], [um, v0], [um, v1], [u0, vm], [u1, vm]];
      if (!c.shaded && samples.some((q) => blocked(G.toUV(N.frame.toLL(q))))) c.shaded = true;
    }
    // area d'ombra da disegnare: proiezione dei vertici superiori sul piano della falda, unita al bordo d'ombra della falda di origine
    for (const P of prisms) {
      const den = tanE - PN.k * (PN.n[0] * dir[0] + PN.n[1] * dir[1]);
      if (den <= 1e-6) continue;
      const proj = P.pts.map((p) => { const t = Math.min((P.top - PN.z(p)) / den, 60); return t > 0 ? [p[0] - dir[0] * t, p[1] - dir[1] * t] : null; }).filter(Boolean);
      const ownT = P.pts.map((p) => { const d2 = tanE - P.PF.k * (P.PF.n[0] * dir[0] + P.PF.n[1] * dir[1]); const t = d2 > 1e-6 ? Math.min((P.top - P.PF.z(p)) / d2, 60) : 60; return [p[0] - dir[0] * t, p[1] - dir[1] * t]; });
      const hull = convexHull([...P.pts, ...proj, ...ownT]);
      if (proj.length && hull.length >= 3) N.shadows.push({ oid: P.oid, pts: hull.map((q) => N.frame.toUV(G.toLL(q))), cross: true });
    }
  }
}

function convexHull(pts) {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cr = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], up = [];
  for (const q of p) { while (lo.length >= 2 && cr(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); }
  for (const q of p.reverse()) { while (up.length >= 2 && cr(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
  return [...lo.slice(0, -1), ...up.slice(0, -1)];
}

export function computeLayout(project) {
  const dims = moduleDims(project);
  const flat = project.roof_type === "piano";
  const fields = (project.roofs || []).filter((r) => (r.polygon || []).length >= 3).map((r) => fieldLayout(project, r, dims));
  if (!flat) {
    crossShadows(project, fields);
    const tanE = Math.tan(sunNoonWinter(project.lat) * D);
    for (const f of fields) f.selfShaded = Math.tan(f.pitch * D) * -Math.cos((Number(f.roof.azimuth) || 0) * D) >= tanE;
  }
  return {
    fields, flat, module: dims.m, du: dims.du, dsl: dims.dsl, gu: Number(project.modules.gap) || 0,
    cells: fields.flatMap((f) => f.cells), active: fields.flatMap((f) => f.active),
    roofArea: fields.reduce((s, f) => s + f.roofArea, 0),
  };
}

// linee dei travetti misurati (u = u_min + primo + i·interasse), in lat/lng
export function rafterLines(roof, defaultSpacing) {
  const rf = roof.rafters || {}, off = Number(rf.offset), sp = Number(rf.spacing || defaultSpacing);
  if (rf.offset === null || rf.offset === undefined || rf.offset === "" || !(off >= 0) || !(sp > 0.1) || (roof.polygon || []).length < 3) return [];
  const fr = makeFrame(roof.polygon, Number(roof.azimuth) || 0), uv = roof.polygon.map(fr.toUV);
  const us = uv.map((q) => q[0]), vs = uv.map((q) => q[1]), v0 = Math.min(...vs), v1 = Math.max(...vs), u1 = Math.max(...us);
  const out = [];
  for (let u = Math.min(...us) + off; u <= u1 + 1e-9 && out.length < 500; u += sp) out.push([fr.toLL([u, v0]), fr.toLL([u, v1])]);
  return out;
}
